import { DestroyRef, Injectable, Signal, inject, signal } from '@angular/core';

type ParentProps = Parameters<Parameters<Window['parentIFrame']['getParentProps']>[0]>[0];

/** Faixa da aplicação que o usuário enxerga, em px do documento do iframe. */
export interface RegiaoVisivel {
  top: number;
  height: number;
}

/**
 * Qual pedaço da aplicação está na tela do usuário.
 *
 * Dentro do portal a aplicação roda num iframe sem rolagem própria, que cresce
 * até a altura do conteúdo (ver `@iframe-resizer/child` em `app.component.ts`).
 * Ali a viewport do iframe é a página inteira, e quem rola é o portal — então
 * `position: fixed` e `100vh` não dizem nada sobre o que está visível. Quem sabe
 * é o portal, e ele conta pelo `getParentProps` do iframe-resizer: posição do
 * iframe e viewport visível, reenviadas a cada scroll ou resize. É postMessage
 * por baixo, então funciona com portal e aplicação em origens diferentes.
 *
 * `null` quando não há portal (dev local, aba própria) ou o iframe está fora da
 * tela: aí a viewport da própria aplicação já é a região visível.
 */
@Injectable({
  providedIn: 'root',
})
export class ParentViewportService {
  private readonly destroyRef = inject(DestroyRef);

  private readonly _regiaoVisivel = signal<RegiaoVisivel | null>(null);

  readonly regiaoVisivel: Signal<RegiaoVisivel | null> = this._regiaoVisivel.asReadonly();

  private pararDeOuvir?: () => void;

  /**
   * Chamado no bootstrap, e não na abertura do modal, para a região já estar
   * conhecida quando ele abrir — senão ele nasceria no meio do iframe e pularia
   * um instante depois. O custo de ouvir sempre é baixo: o portal agrupa os
   * avisos por frame, e sem modal aberto ninguém lê o signal.
   */
  conectar(): void {
    // `parentIFrame` só existe depois do handshake com o portal. Se ainda não
    // houve, o child chama o `onReady` quando houver — e, fora do portal, nunca.
    if (window.parentIFrame) {
      this.ouvirPortal();
    } else {
      const opcoes = window.iFrameResizer;
      window.iFrameResizer = {
        ...opcoes,
        onReady: () => {
          opcoes?.onReady?.();
          this.ouvirPortal();
        },
      };
    }

    this.destroyRef.onDestroy(() => this.pararDeOuvir?.());
  }

  private ouvirPortal(): void {
    if (this.pararDeOuvir) {
      return;
    }

    this.pararDeOuvir = window.parentIFrame.getParentProps((props) =>
      this._regiaoVisivel.set(calcularRegiaoVisivel(props)),
    );
  }
}

/**
 * Interseção entre o iframe e a viewport visível do portal, nas coordenadas do
 * iframe. O `getBoundingClientRect` do iframe é relativo à viewport de layout;
 * o `offsetTop` do `visualViewport` cobre o zoom por pinça no celular.
 */
export function calcularRegiaoVisivel({ iframe, viewport }: ParentProps): RegiaoVisivel | null {
  const top = Math.max(0, viewport.offsetTop - iframe.top);
  const bottom = Math.min(iframe.height, viewport.offsetTop + viewport.height - iframe.top);

  return bottom > top ? { top, height: bottom - top } : null;
}
